


// Vue
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { mapGetters, mapActions } from 'vuex';
import { Mixins } from 'vue-mixin-decorator';

// Third Party
import dayjs, { UnitType } from 'dayjs';
import 'dayjs/locale/es';

// Models
import { Client } from '@/manager/client/client.model';

// Constants
import Assets from '@/constants/assets.ts';

// Components
import Btn, { BtnColor, BtnWidth } from '@/shared/components/btn.component.vue';
import CheckIcon, { CheckIconColor } from '@/shared/components/CheckIcon.component.vue';
import Field from '@/shared/components/field.component.vue';
import FieldSet from '@/shared/components/field-set.component.vue';
import { IconEnum } from '@/shared/components/icon.component.vue';
import Input from '@/shared/components/input.component.vue';
import SearchAutocomplete from '@/shared/components/SearchAutocomplete.component.vue';
import TextArea from '@/shared/components/text-area.component.vue';
import TopModal from '@/shared/components/TopModal.component.vue';
import FlatSelectorText, {
    FlatSelectorTextPredefinedStyle,
} from '@/shared/components/FlatSelectorText.component.vue';

// Mixins
import SearchMixin from '@/shared/mixins/search.mixin.ts';
import ErrorMixin from '@/shared/mixins/error.mixin.ts';

// Services
import UtilsService from '@/core/services/utils.service';
import { ConvocationService, TypeOfProduct } from '@/manager/convocation/convocation.service';


interface MixinInterface extends ErrorMixin, SearchMixin {}

@Component({
    components: {
        Btn,
        CheckIcon,
        Field,
        FieldSet,
        Input,
        SearchAutocomplete,
        TextArea,
        TopModal,
        FlatSelectorText,
    },
    computed: {
        ...mapGetters({
            getProfile: 'getProfile',
            clientList: 'getClientList',
            getProcedencyList: 'procedencyList',
        }),
    },
})
export default class AddPurchaseModal extends Mixins<MixinInterface> (ErrorMixin, SearchMixin) {
    @Prop({ default: true }) private opened!: boolean;
    @Prop() private convocation!: any;
    @Prop({ default: false }) private teamParticipant!: boolean;


    private assets = Assets;
    private btnColor = BtnColor;
    private btnWidth = BtnWidth;
    private iconEnum = IconEnum;
    private checkIconColor = CheckIconColor;
    private utilsService = UtilsService;
    private getProfile!: any;
    private newClient: Client = new Client();
    private lastNoteTyped: string = '';
    private selectedClient: any | null = null;
    private clientList!: Client[];
    private getProcedencyList !: any;
    private nifMask = '';
    private flatSelectorTextPredefinedStyle = FlatSelectorTextPredefinedStyle;
    private procedencyOptions: any = [];
    private procedencySelected: any = {};
    private procedencyOption: any  = {
        value: '0',
        label: '',
    };
    public error = {
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
    };


    @Watch('newClient.user.profile.nif')
    private watchNif(newVal: string, oldVal: string) {
        this.nifMask = UtilsService.setNifMask(newVal, this.nifMask);
    }

    @Watch('procedencyOption')
    private watchProcedency(newVal: any, oldVal: any) {
        (this.newClient.procedency as any) = newVal.value;
    }

    @Watch('newClient.user.profile.phone')
    private watchPhone(newVal: any, oldVal: any) {
        if (!newVal) {
            (this.newClient.user as any).profile.phone = '+';
        } else if (newVal.charAt(0) != '+'){
            newVal = '+' + newVal;
            (this.newClient.user as any).profile.phone = newVal;
        } else {
            (this.newClient.user as any).profile.phone = newVal;
        }  
    }

    private created() {
        this.remainingTime = 0;
        this.fetchProcedency();
    }


    get parsedClientListOptions() {
        return this.clientList.map(
            (client: any) => {
                const option = {
                    id: client.user.id,
                    text: `${client.user.firstName} ${client.user.lastName}`,
                };
                return option;
            },
        );
    }

    get isCourse() {
        return ConvocationService.convocationIsProductType(this.convocation, TypeOfProduct.Course);
    }

    get profileAvatar() {
        let avatar = this.assets.defaultAvatar;
        if (
            this.getProfile.profile &&
            this.getProfile.profile.avatar !== null
        ) {
            avatar = this.getProfile.profile.avatar;
        }
        return avatar;
    }

    get hasSelectedClient() {
        return (this.selectedClient !== null);
    }


    public searchCallback() {
        if (this.searchString === '') {
            this.selectedClient = null;
        }
        this.fetchClientList();
    }

    private isLetter(str: string) {
        return str.length === 1 && str.match(/[a-zA-Z]/i);
    }

    private isNumber(str: string) {
        return str.length === 1 && str.match(/[0-9]/i);
    }

    private fetchClientList() {
        const payload = {
            limit: 20,
            search: this.searchString,
        };
        this.$store.dispatch( 'fetchClientList', payload);
    }

    private closeModal() {
        if ( this.opened ) {
            this.$emit('close-modal-add-participant', false);
        }
    }

    private checkMandatoryFields() {
        let result = true;
        if (this.newClient.user) {
            if (this.newClient.user.firstName === '') {
                this.error.firstName = 'Campo vacío';
                result = false;
            }
            if (this.newClient.user.lastName === '') {
                this.error.lastName = 'Campo vacío';
                result = false;
            }
            if (
                Object.keys(this.newClient.user.profile).length === 0 &&
                this.newClient.user.profile.constructor === Object &&
                this.newClient.user.profile.phone !== ''
            ) {
                this.error.phone = 'Campo vacío';
                result = false;
            }
            if (this.newClient.user.email === '') {
                this.error.email = 'Campo vacío';
                result = false;
            }
        }
        return result;
    }

    private setParticipantFormError(err: any) {
        const serverError = err.response.data;
        if (serverError.user && serverError.user.email[0] !== '') {
            this.error.email = 'Dato incorrecto';
        } else if (serverError.email) {
            this.error.email = 'Ya registrado';
        }
        if (this.error.email !== '' && this.newClient.user) {
            this.newClient.user.email = '';
        }
    }

    private addParticipant() {
        if (this.checkMandatoryFields()) {
            const client = this.newClient;
            (client.user as any).profile.phone = (client.user as any).profile.phone.replaceAll(/\s/g,'');
            const note = { note: this.lastNoteTyped };
            const team = this.teamParticipant;

            if ( client.user !== undefined) {
                delete client.user.profile.avatar;
            }

            this.$store.dispatch(
                'newParticipant',
                {
                    convocationId: this.convocation.id,
                    client,
                    note,
                    team,
                },
            ).then(
                (res) => {
                    this.closeModal();
                    this.lastNoteTyped = '';
                    this.clearClient();
                    setTimeout(() => {
                        this.$emit('new-participant', true);
                    }, 300);
                },
            ).catch((err) => {
                if (err.response.status === 406) {
                    setTimeout(() => {
                        this.$emit('error-new-participant', err.response.data.detail);
                    }, 300);
                } else {
                    this.setParticipantFormError(err);
                }
            });
        }
    }

    private selectClient(option: any) {
        this.selectedClient = option;
        (this.newClient as any).user.profile.nif = '';
        setTimeout(() => {
            this.fillWithClientData();
        }, 100);
    }

    private clearClient() {
        this.selectedClient = null;
        this.newClient = new Client();
        this.procedencySelectedMethod();
    }

    private fillWithClientData() {
        if (this.selectedClient !== null) {
            const client = this.$store.getters.getClient(this.selectedClient.id);
            this.newClient = client;
            this.procedencySelectedMethod();
        }
    }

    private parseProcedencyToFlatSelectorTextOption(procedencyList: any) {
        this.procedencyOptions = procedencyList.map(
            (procedency: any) => {
                const option = {
                    value: procedency.id,
                    label: procedency.name,
                };
                return option;
            },
        );
    }

    private fetchProcedency() {
        this.$store.dispatch('fetchProcedency').then(
            (res) => {
                this.parseProcedencyToFlatSelectorTextOption(this.getProcedencyList);
            },
        );
    }

    private procedencySelectedMethod() {
        const procedencyId = this.newClient.procedency;
        let result = { value: 0, label: '' };
        if ( procedencyId ) {
            result = this.procedencyOptions.find( (option: any) => {
                    return procedencyId && option.value === procedencyId;
                },
            );
        }
        this.procedencySelected = result;
    }
}
